import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, IconButton, Typography } from '@mui/material';
import { memo } from 'react';
import { SmallLoader, Tooltip } from 'framework/ui/mui-builder';
import type { SyberryTodayTaskId, SyberryTodayWorkItem } from 'domain/people/syberryToday/types';
import SyberryTodayTaskDetails from '../SyberryTodayTaskDetails/SyberryTodayTaskDetails';

type SyberryTodayTaskCardProps = {
  task: SyberryTodayWorkItem;
  isExpanded: boolean;
  isSelectionHidden?: boolean;
  isSelectionPending?: boolean;
  isSelectionDisabled?: boolean;
  onToggleSelection?: (taskId: SyberryTodayTaskId) => void;
  onToggleExpansion: (taskId: SyberryTodayTaskId) => void;
  onOpenSpentTime?: (task: SyberryTodayWorkItem) => void;
};

const SyberryTodayTaskCard = ({
  task,
  isExpanded,
  isSelectionHidden = false,
  isSelectionPending = false,
  isSelectionDisabled = false,
  onToggleSelection,
  onToggleExpansion,
  onOpenSpentTime,
}: SyberryTodayTaskCardProps) => {
  const selectionLabel = task.selectedForToday
    ? `Remove task from today: ${task.issueKey} ${task.title}`
    : `Add task to today: ${task.issueKey} ${task.title}`;
  const selectionTooltip = isSelectionPending ? 'Saving task selection' : selectionLabel;
  const selectionIcon = isSelectionPending ? (
    <SmallLoader size="14px" />
  ) : task.selectedForToday ? (
    <CloseIcon fontSize="small" />
  ) : (
    <AddIcon fontSize="small" />
  );

  const renderSelectionButton = () => {
    if (isSelectionHidden || !onToggleSelection) return null;

    return (
      <Tooltip title={selectionTooltip}>
        <Box component="span" sx={{ display: 'inline-flex', alignSelf: 'center' }}>
          <IconButton
            aria-busy={isSelectionPending}
            aria-label={selectionLabel}
            disabled={isSelectionDisabled}
            size="small"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxSizing: 'border-box',
              alignSelf: 'center',
              width: '24px',
              height: '24px',
              padding: 0,
              border: 1,
              borderColor: 'success.main',
              color: 'success.main',
              borderRadius: '50%',
              ...(task.selectedForToday && {
                borderColor: 'secondary.main',
                backgroundColor: 'secondary.main',
                color: 'common.white',
                '&:hover': {
                  backgroundColor: 'secondary.main',
                },
              }),
            }}
            onClick={() => onToggleSelection(task.id)}
          >
            {selectionIcon}
          </IconButton>
        </Box>
      </Tooltip>
    );
  };

  const renderSpentTimeButton = () => {
    if (!onOpenSpentTime) return null;

    return (
      <Tooltip title="Log spent time">
        <IconButton
          aria-label={`Open spent time drawer: ${task.issueKey} ${task.title}`}
          size="small"
          sx={{
            alignSelf: 'center',
            width: '30px',
            height: '30px',
            backgroundColor: 'background.brandSubtle',
            color: 'brand',
            borderRadius: 2,
          }}
          onClick={() => onOpenSpentTime(task)}
        >
          <AccessTimeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        padding: '16px 14px 14px',
        border: 1,
        borderColor: 'border.separator',
        borderRadius: '4px',
        backgroundColor: 'background.paper',
        boxShadow:
          '0 2px 6px 0 var(--mui-palette-common-shadowMedium), 0 1px 2px 0 var(--mui-palette-common-shadowStrong)',
        overflow: 'hidden',
        ...(task.isUnread && {
          '&::after': {
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: 0,
            height: 0,
            borderBottom: '16px solid',
            borderBottomColor: 'success.main',
            borderLeft: '16px solid transparent',
            content: '""',
          },
        }),
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 2,
          display: 'grid',
          gridTemplateColumns: isSelectionHidden ? 'minmax(0, 1fr) auto' : '28px minmax(0, 1fr) auto',
          gap: 1.25,
          alignItems: 'center',
        }}
      >
        {renderSelectionButton()}

        <Box sx={{ minWidth: 0 }}>
          <Typography
            component="a"
            href={task.redmineUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open in Redmine: ${task.issueKey} ${task.title}`}
            variant="body1"
            color="textPrimary"
            sx={{
              display: 'block',
              minWidth: 0,
              textDecoration: 'none',
              whiteSpace: isExpanded ? 'normal' : 'nowrap',
              overflow: isExpanded ? 'visible' : 'hidden',
              textOverflow: isExpanded ? 'unset' : 'ellipsis',
              ...(isExpanded && {
                overflowWrap: 'anywhere',
              }),
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {task.title}
          </Typography>

          <Typography variant="body2" color="textSecondary" mt={0.5} noWrap>
            {task.projectName} / {task.streamName}
          </Typography>
        </Box>

        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          {renderSpentTimeButton()}

          <Tooltip title={isExpanded ? 'Collapse task details' : 'Expand task details'}>
            <IconButton
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} task details: ${task.issueKey} ${task.title}`}
              size="small"
              sx={{
                alignSelf: 'center',
                width: '28px',
                height: '28px',
                color: 'text.primary',
              }}
              onClick={() => onToggleExpansion(task.id)}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {isExpanded && <SyberryTodayTaskDetails task={task} />}
    </Box>
  );
};

export default memo(SyberryTodayTaskCard);
